import { Group, Title } from "@mantine/core";
import { YearPickerInput } from "@mantine/dates";
import { useSessionStorage } from "@mantine/hooks";
import { IconCalendarWeek } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { ContentWithSkeleton } from "../../components/ContentWithSkeleton/ContentWithSkeleton";
import { LoadOrError } from "../../components/LoadOrError/LoadOrError";
import { useCurrentUserQuery } from "../../hooks/useCurrentUserQuery";
import { useProjects } from "../../hooks/useProjects";
import { StartPageTileSize } from "../../types/api.types";
import type { SiteName } from "../../utils/enums";
import { useStartPageTiles } from "./hooks/useStartPageTiles";
import { MySitesInNumbers } from "./MySitesInNumbers/MySitesInNumbers";
import { MySitesManagement } from "./MySitesManagement/MySitesManagement";
import { ProjectSelection } from "./ProjectSelection";
import { StartPageSection } from "./StartPageSection";

declare const SITE_NAME: SiteName;

function StartPage() {
  const urlParams = new URLSearchParams(window.location.search);
  const urlProjectId = urlParams.get("projectId");

  const [storedProjectId, setStoredProjectId, removeStoredProjectId] =
    useSessionStorage<string | undefined>({
      key: "startPageProjectId",
      defaultValue: undefined
    });
  const [storedYear, setStoredYear] = useSessionStorage<number | undefined>({
    key: "startPageYear",
    defaultValue: undefined
  });

  const { data: projects, error: projectsError } = useProjects({
    isFilterActive: true
  });

  const currentYear = new Date().getFullYear();

  const [projectId, setProjectId] = useState<string | undefined>(
    urlProjectId || storedProjectId || undefined
  );
  const [year, setYear] = useState<number>(storedYear || currentYear);

  const { tiles, error: tilesError } = useStartPageTiles(projectId);

  const { currentUser, error: userError } = useCurrentUserQuery();

  useEffect(() => {
    // need to set this here because there should be no breadcrumb (which usually sets the title)
    document.title = `Startseite - ${SITE_NAME}`;
  }, []);

  useEffect(() => {
    if (urlProjectId) {
      setProjectId(urlProjectId);
      setStoredProjectId(urlProjectId);
    } else if (storedProjectId) {
      setProjectId(storedProjectId);
      const newUrlParams = new URLSearchParams(window.location.search);
      newUrlParams.set("projectId", storedProjectId);
      window.history.replaceState(null, "", "?" + newUrlParams.toString());
    }
  }, [urlProjectId, storedProjectId, setStoredProjectId]);

  function handleChangeProject(newProjectId?: string) {
    setProjectId(newProjectId);
    if (newProjectId) setStoredProjectId(newProjectId);
    else removeStoredProjectId(); // undefined gets stringified in useEffect, this is a quick fix for that issue and also removes the projectId from session storage when "Alle Projekte" is selected

    const newUrlParams = new URLSearchParams(window.location.search);
    if (newProjectId) {
      newUrlParams.set("projectId", newProjectId);
    } else {
      newUrlParams.delete("projectId");
    }
    window.history.replaceState(null, "", "?" + newUrlParams.toString());
  }

  function handleChangeYear(newYearDate: string | null) {
    if (!newYearDate) {
      return;
    }
    setYear(new Date(newYearDate).getFullYear());
    setStoredYear(new Date(newYearDate).getFullYear());
  }

  const allProjectsChoice = { id: undefined, name: "Alle Projekte" };

  const yearDate = new Date(year, 0, 1);
  const maxDate = new Date(currentYear, 11, 31);

  return (
    <div className="StartPage">
      <LoadOrError error={userError || projectsError}>
        <Group justify="space-between">
          <Title fw={500} order={3}>
            {`Willkommen${currentUser?.name ? `, ${currentUser.name}` : ""}!`}
          </Title>
          <ContentWithSkeleton isLoading={!projects} miw={500}>
            <ProjectSelection
              projectId={projectId}
              projects={[allProjectsChoice, ...(projects || [])]}
              onChange={handleChangeProject}
            />
          </ContentWithSkeleton>
        </Group>
        <LoadOrError error={tilesError}>
          <StartPageSection
            className="MySitesInNumbersSection"
            filter={
              <YearPickerInput
                leftSection={<IconCalendarWeek size="1.2em" />}
                maxDate={maxDate}
                value={yearDate}
                onChange={handleChangeYear}
              />
            }
            title="Meine Liegenschaften in Zahlen"
          >
            <MySitesInNumbers
              projectId={projectId}
              tiles={tiles?.filter(
                (tile) => tile.size === StartPageTileSize.Small
              )}
              year={year}
            />
          </StartPageSection>
          <StartPageSection
            className="MySitesManagementSection"
            title="Verwaltung meiner Liegenschaften"
          >
            <MySitesManagement
              projectId={projectId}
              tiles={tiles?.filter(
                (tile) => tile.size !== StartPageTileSize.Small
              )}
            />
          </StartPageSection>
        </LoadOrError>
      </LoadOrError>
    </div>
  );
}

export { StartPage };
